import React, { useState, useMemo, useRef, useCallback } from 'react';
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  ColumnFiltersState,
  SortingState,
} from '@tanstack/react-table';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Search } from 'lucide-react';
import { type PriceSheetItem } from '@/app/services/pricingService';
import { getColumns } from './columns';
import { PriceInput } from './PriceInput';

interface BulkPriceUpdateTableProps {
  data: PriceSheetItem[];
  modifiedItems: Map<number, number>;
  selectedItems: PriceSheetItem[];
  onPriceChange: (itemId: number, price: number) => void;
  onSelectionChange: (items: PriceSheetItem[]) => void;
}

export default function BulkPriceUpdateTable({
  data,
  modifiedItems,
  selectedItems,
  onPriceChange,
  onSelectionChange,
}: BulkPriceUpdateTableProps) {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [globalFilter, setGlobalFilter] = useState('');
  const [rowSelection, setRowSelection] = useState({});
  const [activeCell, setActiveCell] = useState<{ row: number; col: string } | null>(null);
  const cellRefs = useRef<Map<string, HTMLElement>>(new Map());

  // Focus management
  const focusCell = useCallback((rowIndex: number, colId: string) => {
    const key = `${rowIndex}-${colId}`;
    const element = cellRefs.current.get(key);
    if (element) {
      element.focus();
      setActiveCell({ row: rowIndex, col: colId });
    }
  }, []);

  // Keyboard navigation handler - moved before columns definition
  const handleKeyDown = useCallback((e: React.KeyboardEvent, rowIndex: number, colId: string) => {
    switch (e.key) {
      case 'ArrowUp':
        e.preventDefault();
        if (rowIndex > 0) {
          focusCell(rowIndex - 1, colId);
        }
        break;
      case 'ArrowDown':
        e.preventDefault();
        // Navigate within the current page (will be checked against actual row count in render)
        focusCell(rowIndex + 1, colId);
        break;
      case 'Tab':
        // Let default tab behavior work but could enhance later
        break;
      case 'Enter':
        e.preventDefault();
        // Move to next row on Enter
        focusCell(rowIndex + 1, colId);
        break;
      case 'Escape':
        e.preventDefault();
        // Clear focus
        (e.target as HTMLElement).blur();
        setActiveCell(null);
        break;
    }
  }, [focusCell]);

  // Define columns with keyboard navigation support
  const columns = useMemo(
    () => getColumns({ 
      modifiedItems, 
      onPriceChange,
      activeCell,
      onCellFocus: focusCell,
      onKeyDown: handleKeyDown,
      cellRefs
    }),
    [modifiedItems, onPriceChange, activeCell, focusCell, handleKeyDown]
  );

  // Sort data to bring modified items to the top
  const sortedData = useMemo(() => {
    // Create a stable sort that preserves item order within modified/unmodified groups
    const modified: PriceSheetItem[] = [];
    const unmodified: PriceSheetItem[] = [];
    
    data.forEach(item => {
      if (modifiedItems.has(item.item_id)) {
        modified.push(item);
      } else {
        unmodified.push(item);
      }
    });
    
    return [...modified, ...unmodified];
  }, [data, modifiedItems.size]); // Only re-sort when the number of modified items changes

  const table = useReactTable({
    data: sortedData,
    columns,
    state: {
      sorting,
      columnFilters,
      globalFilter,
      rowSelection,
    },
    enableRowSelection: true,
    onRowSelectionChange: setRowSelection,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  // Clear cell refs when page changes
  React.useEffect(() => {
    cellRefs.current.clear();
  }, [table.getState().pagination.pageIndex]);

  // Update parent selection state when row selection changes
  React.useEffect(() => {
    const selectedRows = table.getFilteredSelectedRowModel().rows;
    const selectedData = selectedRows.map(row => row.original);
    onSelectionChange(selectedData);
  }, [rowSelection, table, onSelectionChange]);

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1 sm:max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search items..."
            value={globalFilter ?? ''}
            onChange={(e) => setGlobalFilter(e.target.value)}
            className="pl-9 text-sm"
          />
        </div>
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-4 text-xs sm:text-sm text-muted-foreground">
          <span>
            {table.getFilteredSelectedRowModel().rows.length} of{' '}
            {table.getFilteredRowModel().rows.length} selected
          </span>
          {modifiedItems.size > 0 && (
            <span className="text-warning font-medium">
              {modifiedItems.size} modified
            </span>
          )}
        </div>
      </div>

      <div className="rounded-md border overflow-x-auto">
        <div className="min-w-[600px]">
          <Table>
            <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row, displayIndex) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && "selected"}
                  className={modifiedItems.has(row.original.item_id) ? "bg-warning/5" : ""}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {cell.column.id === 'new_price' ? (
                        <PriceInput
                          key={`price-input-${row.original.item_id}`}
                          itemId={row.original.item_id}
                          currentPrice={parseFloat(row.original.price) || 0}
                          modifiedPrice={modifiedItems.get(row.original.item_id)}
                          onPriceChange={onPriceChange}
                          isActive={activeCell?.row === displayIndex && activeCell?.col === 'new_price'}
                          onFocus={() => focusCell(displayIndex, 'new_price')}
                          onKeyDown={(e) => handleKeyDown(e, displayIndex, 'new_price')}
                          ref={(el) => {
                            if (el) {
                              cellRefs.current.set(`${displayIndex}-new_price`, el);
                            }
                          }}
                        />
                      ) : (
                        flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  No results.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-4">
          <div className="text-xs sm:text-sm text-muted-foreground">
            {table.getFilteredRowModel().rows.length > 0 ? (
              <>
                {table.getState().pagination.pageIndex * table.getState().pagination.pageSize + 1}-
                {Math.min(
                  (table.getState().pagination.pageIndex + 1) * table.getState().pagination.pageSize,
                  table.getFilteredRowModel().rows.length
                )}
                {' of '}
                {table.getFilteredRowModel().rows.length}
              </>
            ) : (
              '0 entries'
            )}
          </div>
          <div className="hidden sm:flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Show</span>
            <Select
              value={table.getState().pagination.pageSize.toString()}
              onValueChange={(value) => {
                table.setPageSize(Number(value));
                table.setPageIndex(0); // Reset to first page when changing page size
              }}
            >
              <SelectTrigger className="h-8 w-[70px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="10">10</SelectItem>
                <SelectItem value="20">20</SelectItem>
                <SelectItem value="50">50</SelectItem>
              </SelectContent>
            </Select>
            <span className="text-sm text-muted-foreground">per page</span>
          </div>
        </div>
        <div className="flex items-center gap-1 sm:gap-2">
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8 sm:h-9 sm:w-9 hidden sm:flex"
            onClick={() => table.setPageIndex(0)}
            disabled={!table.getCanPreviousPage()}
          >
            <ChevronsLeft className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8 sm:h-9 sm:w-9"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="text-xs sm:text-sm px-1 sm:px-2">
            {table.getPageCount() > 0 ? (
              <>
                <span className="hidden sm:inline">Page </span>
                {table.getState().pagination.pageIndex + 1}
                <span className="hidden sm:inline"> of </span>
                <span className="sm:hidden">/</span>
                {table.getPageCount()}
              </>
            ) : (
              '0'
            )}
          </span>
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8 sm:h-9 sm:w-9"
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8 sm:h-9 sm:w-9 hidden sm:flex"
            onClick={() => table.setPageIndex(table.getPageCount() - 1)}
            disabled={!table.getCanNextPage()}
          >
            <ChevronsRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}